import { Component, computed, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterOutlet, RouterModule } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';

import { AuthService } from '../../core/services/auth.service';
import { ApiService } from '../../core/services/api.service';
import { Branch } from '../../core/models';
import { LanguageService } from '../../core/services/language.service';
import { ThemeService } from '../../core/services/theme.service';
import { ThemeSwitcherComponent } from '../../shared/theme-switcher/theme-switcher.component';
import { LanguageSwitcherComponent } from '../../shared/language-switcher/language-switcher.component';
import { PlanLimitDialogComponent } from '../../shared/plan-limit-dialog/plan-limit-dialog.component';
import { NavGroup, PortalRole, portalFor, visibleNav } from './nav';
import {
  LucideAngularModule, LayoutDashboard, Building2, Users, Settings, LogOut, ChevronDown, Bell, Menu,
  GraduationCap, HeartHandshake, Calendar, Layers, BookOpen, CalendarDays, Megaphone, ClipboardList, Wallet,
  ClipboardCheck, FileText, Award, ShieldCheck, KeyRound, UserCog, UserRound, PenLine, ArrowUpRight, ScrollText, X, AlertTriangle, ListChecks,
  School, Plane, UserPlus, FileBadge, MessageSquare
} from 'lucide-angular';

export type { NavGroup, NavItem, PortalRole } from './nav';

@Component({
  selector: 'app-main-layout',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterOutlet, RouterModule, LucideAngularModule,
    TranslocoModule, LanguageSwitcherComponent, ThemeSwitcherComponent, PlanLimitDialogComponent],
  templateUrl: './main-layout.component.html',
  styleUrl: './main-layout.component.css'
})
export class MainLayoutComponent {
  private readonly transloco = inject(TranslocoService);
  readonly language = inject(LanguageService);
  private readonly theme = inject(ThemeService);

  /**
   * Bring the account and this device into line the moment somebody arrives.
   * A choice made on the sign-in screen has no account to be saved against
   * when it is made, so it is saved here instead.
   */
  private readonly syncLanguage = effect(() => {
    const user = this.auth.user();
    if (!user) {
      return;
    }
    const toSave = this.language.reconcile(user.language);
    if (toSave) {
      this.api.updateMyProfile({ language: toSave }).subscribe({
        error: () => undefined // The screen is already in the right language.
      });
    }
  });

  /** The same for the colour theme: the account's on a new device, this device's newer choice otherwise. */
  private readonly syncTheme = effect(() => {
    const user = this.auth.user();
    if (!user) {
      return;
    }
    const toSave = this.theme.reconcile(user.theme);
    if (toSave) {
      this.api.updateMyProfile({ theme: toSave }).subscribe({
        error: () => undefined // The screen is already in the right colours.
      });
    }
  });

  readonly Settings = Settings;
  readonly UserRound = UserRound;
  readonly LogOut = LogOut;
  readonly ChevronDown = ChevronDown;
  readonly Bell = Bell;
  readonly Menu = Menu;
  readonly Building2 = Building2;
  readonly ShieldCheck = ShieldCheck;
  readonly X = X;
  readonly AlertTriangle = AlertTriangle;

  readonly auth = inject(AuthService);
  private readonly api = inject(ApiService);

  /** Desktop: expanded (256px) or icon-only (68px). */
  sidebarOpen = true;

  /** The branches a school-wide admin can switch between. Empty for everyone
   *  else — their branch is fixed on their account. */
  readonly branchOptions = signal<Branch[]>([]);
  switchingBranch = false;

  constructor() {
    // Re-read the session from the server once, when the shell mounts.
    //
    // Permissions are cached in localStorage from the moment somebody signed
    // in, and the route guards read that cache. So a capability granted to a
    // role afterwards is invisible to anyone already signed in: their nav
    // shows the new item, the guard says no, and clicking it bounces them
    // silently back to their dashboard. Asking the server on each load is one
    // request per session and makes the cache self-healing.
    //
    // Failure is ignored on purpose — offline or a blip should not sign
    // somebody out; the stored session stays as it was.
    this.auth.refreshUser().subscribe({ next: () => { }, error: () => { } });

    // Load the branch list once a school-wide admin is signed in, so the top
    // bar can offer the switcher.
    effect(() => {
      const user = this.auth.user();
      if (user?.role === 'SCHOOL_ADMIN' && user.school_id !== null && !this.branchOptions().length) {
        this.api.listBranches(user.school_id).subscribe({
          next: rows => this.branchOptions.set(rows),
          error: () => this.branchOptions.set([])
        });
      }
    });
  }

  get isSchoolWideAdmin(): boolean {
    return this.auth.user()?.role === 'SCHOOL_ADMIN';
  }

  switchBranch(branchId: number): void {
    if (branchId === this.auth.effectiveBranchId() || this.switchingBranch) {
      return;
    }
    this.switchingBranch = true;
    this.auth.setActiveBranch(branchId);
    // Land on the dashboard for the new branch, then reload so every screen
    // re-runs its own load against the new branch header. refreshUser first,
    // so the reloaded page already has the new branch's name and year.
    this.auth.refreshUser().subscribe({
      next: () => {
        window.location.href = '/school-admin/dashboard';
      },
      error: () => {
        window.location.href = '/school-admin/dashboard';
      }
    });
  }

  /** Mobile: the same sidebar, as a drawer over the content. */
  mobileNavOpen = false;

  /** The portal to render, derived from the signed-in user's role. */
  readonly role = computed<PortalRole>(() => portalFor(this.auth.role()));

  /** The app's own mark, for a school that has not uploaded one. SVG, so it has no background box of its own. */
  private readonly fallbackLogo = 'logo.svg';
  /** Set when the school's crest will not load, so we stop asking for it. */
  private logoFailed = false;

  /**
   * What sits in the corner of the sidebar.
   *
   * The school's crest belongs to the people using the app — its staff, its
   * families — so it wins wherever there is one. A platform administrator
   * belongs to no school and sees ours, and so does anyone whose school has
   * not uploaded a crest yet.
   */
  get brandLogo(): string {
    const school = this.auth.user()?.school_logo_url;
    return school && !this.logoFailed ? school : this.fallbackLogo;
  }

  onLogoError(): void {
    this.logoFailed = true;
  }

  /** Support context is a Super Admin capability (P1-SCR-29). */
  supportContext: { school: string; branch: string; year: string; minutesLeft: number } | null = null;

  get navGroups(): NavGroup[] {
    return visibleNav(this.role(), code => this.auth.can(code));
  }


  /** The tenant scope the API is answering in, straight from /auth/me. */
  get contextLabel(): string {
    const user = this.auth.user();
    if (!user) {
      return '';
    }
    if (user.role === 'SUPER_ADMIN' && !user.school_name) {
      return this.transloco.translate('shell.platformView');
    }
    // A parent is not working inside one school's tenant \u2014 their portal shows
    // their children wherever those children are admitted, which can be more
    // than one school. A single school/branch/year label would be a claim
    // about scope that does not hold.
    if (user.role === 'PARENT') {
      return this.transloco.translate('shell.familyView');
    }
    return [user.school_name, user.branch_name, user.academic_year_name]
      .filter(Boolean)
      .join(' \u00b7 ');
  }

  get userName(): string {
    return this.auth.user()?.full_name ?? '';
  }

  /** The signed-in person's photograph, or null to fall back to initials. */
  get userPhoto(): string | null {
    const url = this.auth.user()?.photo_url ?? null;
    return url && !this.failedPhoto ? url : null;
  }

  /** A stored URL is no guarantee the file loads; a broken icon beside
   *  somebody's own name is worse than the initials it replaced. */
  failedPhoto = false;

  onPhotoError(): void {
    this.failedPhoto = true;
  }

  get userInitials(): string {
    const name = this.auth.user()?.full_name ?? '';
    const parts = name.split(' ').filter(Boolean);
    if (parts.length === 0) {
      return '?';
    }
    const first = parts[0].charAt(0);
    const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
    return (first + last).toUpperCase();
  }

  /** SUPER_ADMIN reads badly in a footer; School admin reads as a person. */
  /**
   * The role under the avatar, from the catalogue rather than from English
   * string surgery on the enum. `SCHOOL_ADMIN` used to be lower-cased,
   * underscore-stripped and re-capitalised, which produces nothing at all in
   * Kannada.
   */
  get roleLabel(): string {
    return this.transloco.translate(`role.${this.auth.user()?.role ?? 'unknown'}`);
  }


  logout(): void {
    this.auth.logout();
  }

  toggleSidebar(): void {
    this.sidebarOpen = !this.sidebarOpen;
  }

  openMobileNav(): void {
    this.mobileNavOpen = true;
  }

  closeMobileNav(): void {
    this.mobileNavOpen = false;
  }

  exitSupportContext(): void {
    this.supportContext = null;
  }
}
